// Options screen (UI-6): render distance, GUI scale, invert-Y, master/music/sound
// volumes, the subtitles toggle, and the M24 keybind remapper. Mutates the shared
// settings object live; `apply` re-applies to the game (GUI scale + audio settings,
// AUD-1/M23). input.ts reads settings.keybinds live, so remaps need no `apply`.
import { VIEW } from '../../../sim/config';
import { h } from '../h';
import { saveSettings, type Settings } from '../../settings';
import { clampFov, fovLabel, FOV_MIN, FOV_MAX } from '../../fov';
import { BINDABLE, keyLabel, rebind, resetBinds, type GameAction } from '../../keybinds';

export function showOptions(settings: Settings, apply: () => void, onClose: () => void): void {
  const root = h('div', { class: 'bb-screen bb-pause' });
  document.body.append(root);

  // ── Controls (M24): click a row, press a key. Rebinding a taken key clears the
  //    other action (conflict resolution in keybinds.rebind). A capture-phase
  //    listener grabs the next keydown ahead of the game's own handler. ──
  let capturing: GameAction | null = null;
  const bindButtons = new Map<GameAction, HTMLButtonElement>();
  const refreshBinds = (): void => {
    for (const b of BINDABLE) {
      const btn = bindButtons.get(b.action)!;
      btn.textContent = capturing === b.action ? '> press a key <' : keyLabel(settings.keybinds[b.action]);
      btn.classList.toggle('bb-bind-active', capturing === b.action);
    }
  };
  const onCapture = (e: KeyboardEvent): void => {
    if (!capturing) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.code !== 'Escape') settings.keybinds = rebind(settings.keybinds, capturing, e.code);
    capturing = null;
    refreshBinds();
  };
  document.addEventListener('keydown', onCapture, true);
  const controls = BINDABLE.map((b) => {
    const btn = h('button', { class: 'bb-btn bb-bind' }) as HTMLButtonElement;
    bindButtons.set(b.action, btn);
    btn.onclick = () => { capturing = b.action; refreshBinds(); };
    return h('div', { class: 'bb-bind-row' }, h('span', { class: 'bb-label' }, b.label), btn);
  });
  const resetControls = h('button', { class: 'bb-btn' }, 'Reset Defaults') as HTMLButtonElement;
  resetControls.onclick = () => { settings.keybinds = resetBinds(); capturing = null; refreshBinds(); };
  refreshBinds();

  // FOV — the render loop reads settings.fov live each frame, so no `apply` needed.
  const fovLabelEl = h('div', { class: 'bb-label' }, `FOV: ${fovLabel(settings.fov)}`);
  const fov = h('input', { class: 'bb-input', type: 'range', min: String(FOV_MIN), max: String(FOV_MAX), value: String(settings.fov) });
  fov.oninput = () => {
    settings.fov = clampFov(Number(fov.value));
    fovLabelEl.textContent = `FOV: ${fovLabel(settings.fov)}`;
  };

  const rdLabel = h('div', { class: 'bb-label' }, `Render distance: ${settings.renderDistance}`);
  const rd = h('input', { class: 'bb-input', type: 'range', min: '4', max: String(VIEW.RENDER_DIST_MAX), value: String(settings.renderDistance) });
  rd.oninput = () => {
    settings.renderDistance = Number(rd.value);
    rdLabel.textContent = `Render distance: ${settings.renderDistance}`;
    apply();
  };

  const gsLabel = h('div', { class: 'bb-label' }, `GUI scale: ${settings.guiScale.toFixed(2)}×`);
  const gs = h('input', { class: 'bb-input', type: 'range', min: '0.75', max: '2', step: '0.25', value: String(settings.guiScale) });
  gs.oninput = () => {
    settings.guiScale = Number(gs.value);
    gsLabel.textContent = `GUI scale: ${settings.guiScale.toFixed(2)}×`;
    apply();
  };

  const invert = h('input', { type: 'checkbox', checked: settings.invertY }) as HTMLInputElement;
  invert.onchange = () => { settings.invertY = invert.checked; };

  const master = h('input', { class: 'bb-input', type: 'range', min: '0', max: '1', step: '0.05', value: String(settings.masterVolume) });
  master.oninput = () => { settings.masterVolume = Number(master.value); apply(); };
  const music = h('input', { class: 'bb-input', type: 'range', min: '0', max: '1', step: '0.05', value: String(settings.musicVolume) });
  music.oninput = () => { settings.musicVolume = Number(music.value); apply(); };
  const sound = h('input', { class: 'bb-input', type: 'range', min: '0', max: '1', step: '0.05', value: String(settings.soundVolume) });
  sound.oninput = () => { settings.soundVolume = Number(sound.value); apply(); };

  const subs = h('input', { type: 'checkbox', checked: settings.subtitles }) as HTMLInputElement;
  subs.onchange = () => { settings.subtitles = subs.checked; apply(); };

  const done = (): void => {
    document.removeEventListener('keydown', onCapture, true);
    saveSettings(settings);
    root.remove();
    onClose();
  };

  root.append(
    h('div', { class: 'bb-title' }, 'Options'),
    h(
      'div',
      { class: 'bb-panel bb-options-panel' },
      fovLabelEl, fov,
      rdLabel, rd,
      gsLabel, gs,
      h('label', { class: 'bb-row' }, invert, h('span', null, 'Invert mouse Y')),
      h('div', { class: 'bb-label' }, 'Master volume'), master,
      h('div', { class: 'bb-label' }, 'Music volume'), music,
      h('div', { class: 'bb-label' }, 'Sound volume'), sound,
      h('label', { class: 'bb-row' }, subs, h('span', null, 'Subtitles')),
      h('div', { class: 'bb-label' }, 'Controls'),
      ...controls,
      resetControls,
      h('button', { class: 'bb-btn bb-primary', onclick: done }, 'Done'),
    ),
  );
}
